import { parseMultiplication } from "./parseMultiplication";
import { parsePower } from "./parsePower";
import { parseResult } from "./parseResult";
import { parseSympyFunction } from "./parseSympyFunction";
import { parseSympyOperator } from "./parseSympyOperator";

import type { SympyResult } from "../types";
import type { NodeJson } from "@corca/data";

/**
 * Parses SymPy JSON results back into NodeJson. The `symbolNodes` map (original
 * input nodes keyed by symbol name) is held as an instance field instead of being
 * threaded through every method.
 */
export class SympyParser {
  warnings: string[] = [];

  constructor(
    readonly symbolNodes?: Map<string, NodeJson>,
    readonly preferDecimal?: boolean,
  ) {}

  warn(message: string): void {
    if (!this.warnings.includes(message)) this.warnings.push(message);
  }

  // Pure renderer: base ** exponent. Negative / rational / unit exponents have already been
  // rewritten to Frac / Root by normalizeSemantic, so no reciprocal or root handling is needed here.
  parsePower(baseArgs: SympyResult, exponentArgs: SympyResult): NodeJson[] {
    return parsePower(this, baseArgs, exponentArgs);
  }

  parseMultiplication(args: SympyResult[]): NodeJson[] {
    return parseMultiplication(this, args);
  }

  parseSympyOperator(
    sympy: Extract<SympyResult, { type: "operator" }>,
    opts?: { parent?: SympyResult; grouped?: boolean; exponent?: boolean },
  ): NodeJson[] {
    return parseSympyOperator(this, sympy, opts);
  }

  parseSympyFunction(
    sympy: Extract<SympyResult, { type: "function" }>,
    opts?: { signed?: boolean; parent?: SympyResult },
  ): NodeJson[] {
    return parseSympyFunction(this, sympy, opts);
  }

  parseResult(
    sympy: SympyResult,
    opts?: { signed?: boolean; parent?: SympyResult; grouped?: boolean; exponent?: boolean },
  ): NodeJson[] {
    return parseResult(this, sympy, opts);
  }
}
